import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Paperclip, Send, X } from "lucide-react";

export interface ComposeDefaults {
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  replyToEmailId?: string;
  /** Previous message, quoted below the reply */
  quote?: { author: string; date: string; text: string };
}

interface ComposeEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  dealName: string;
  contactName?: string;
  defaults: ComposeDefaults;
  onSent: () => void;
}

interface PendingFile {
  file_name: string;
  content_type: string;
  content_base64: string;
  size: number;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_TOTAL_BYTES = 7 * 1024 * 1024;

const splitAddresses = (value: string) =>
  value
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Plain text -> editor HTML: blank lines become paragraphs, single line breaks become <br> */
const textToHtml = (text: string) =>
  text
    .split(/\n{2,}/)
    .map((para) => `<p>${escapeHtml(para).replace(/\n/g, "<br>")}</p>`)
    .join("");

type TemplateKey = "blank" | "follow_up" | "meeting" | "thanks";

const TEMPLATES: Record<TemplateKey, { label: string; subject: (deal: string) => string; body: (name: string, sender: string) => string }> = {
  blank: { label: "Blank email", subject: () => "", body: (name, sender) => `Dear ${name},\n\n\n\nRegards,\n${sender}` },
  follow_up: {
    label: "Follow-up",
    subject: (deal) => `Following up: ${deal}`,
    body: (name, sender) =>
      `Dear ${name},\n\nI wanted to follow up on our recent discussion. Please let me know if you have any questions or need any more details from our side.\n\nRegards,\n${sender}`,
  },
  meeting: {
    label: "Meeting request",
    subject: (deal) => `Meeting request: ${deal}`,
    body: (name, sender) =>
      `Dear ${name},\n\nCould we schedule a short meeting this week to discuss the next steps? Please share a time that works for you.\n\nRegards,\n${sender}`,
  },
  thanks: {
    label: "Thank you",
    subject: () => "Thank you",
    body: (name, sender) =>
      `Dear ${name},\n\nThank you for your time today. As discussed, I will share the details shortly.\n\nRegards,\n${sender}`,
  },
};

const readAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export function ComposeEmailDialog({
  open,
  onOpenChange,
  dealId,
  dealName,
  contactName,
  defaults,
  onSent,
}: ComposeEmailDialogProps) {
  const { success, error } = useToast();
  const { user } = useAuth();
  const fileInput = useRef<HTMLInputElement>(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [text, setText] = useState("");
  const [template, setTemplate] = useState<TemplateKey>("blank");
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [sending, setSending] = useState(false);

  const isReply = Boolean(defaults.replyToEmailId);
  const senderName = `${user?.FirstName || ""} ${user?.LastName || ""}`.trim();
  const greetingName = contactName || "Sir/Madam";

  const setBodyFromText = (plain: string) => {
    setHtml(textToHtml(plain));
    setText(plain);
  };

  useEffect(() => {
    if (!open) return;
    setTo(defaults.to);
    setCc(defaults.cc || "");
    setBcc(defaults.bcc || "");
    setShowCc(Boolean(defaults.cc));
    setShowBcc(Boolean(defaults.bcc));
    setSubject(defaults.subject);
    setTemplate("blank");
    setFiles([]);

    if (isReply) {
      const q = defaults.quote;
      const quoted = q
        ? `<p>On ${escapeHtml(q.date)}, ${escapeHtml(q.author)} wrote:</p><blockquote>${textToHtml(q.text.trim())}</blockquote>`
        : "";
      setHtml(`<p></p><p>Regards,<br>${escapeHtml(senderName)}</p>${quoted}`);
      setText(`\n\nRegards,\n${senderName}${q ? `\n\nOn ${q.date}, ${q.author} wrote:\n> ${q.text.trim().replace(/\n/g, "\n> ")}` : ""}`);
    } else {
      setBodyFromText(TEMPLATES.blank.body(greetingName, senderName));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaults]);

  const applyTemplate = (key: TemplateKey) => {
    setTemplate(key);
    const t = TEMPLATES[key];
    const s = t.subject(dealName);
    if (s) setSubject(s);
    setBodyFromText(t.body(greetingName, senderName));
  };

  const addFiles = async (list: FileList | null) => {
    if (!list) return;
    try {
      const added: PendingFile[] = [];
      for (const f of Array.from(list)) {
        added.push({
          file_name: f.name,
          content_type: f.type || "application/octet-stream",
          content_base64: await readAsBase64(f),
          size: f.size,
        });
      }
      const next = [...files, ...added];
      if (next.reduce((sum, f) => sum + f.size, 0) > MAX_TOTAL_BYTES) {
        error("Attachments too large", "Keep the total under 7 MB");
        return;
      }
      setFiles(next);
    } catch {
      error("Could not read file");
    } finally {
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const toList = splitAddresses(to);
    const ccList = showCc ? splitAddresses(cc) : [];
    const bccList = showBcc ? splitAddresses(bcc) : [];
    const bad = [...toList, ...ccList, ...bccList].find((a) => !EMAIL_RE.test(a));
    if (!toList.length) return error("Missing recipient", "Add at least one email address");
    if (bad) return error("Invalid Email", `"${bad}" is not a valid address`);
    if (!text.trim()) return error("Empty message", "Write a message before sending");

    setSending(true);
    try {
      await api.post(`/api/DealEmail/Send/${dealId}`, {
        to: toList,
        cc: ccList.length ? ccList : undefined,
        bcc: bccList.length ? bccList : undefined,
        subject: subject.trim(),
        body: text,
        body_html: html,
        reply_to_email_id: defaults.replyToEmailId,
        attachments: files.length
          ? files.map(({ file_name, content_type, content_base64 }) => ({ file_name, content_type, content_base64 }))
          : undefined,
      });
      success("Email sent", `Sent to ${toList.join(", ")}`);
      onOpenChange(false);
      onSent();
    } catch (err: any) {
      error("Send failed", err.message || "Could not send the email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl md:max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="size-5 text-primary" />
            {isReply ? "Reply" : "New Email"}
          </DialogTitle>
          <DialogDescription>Sent from your connected mailbox and saved on this deal.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSend} className="space-y-3.5 py-1">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs sm:text-sm font-medium">To *</Label>
              <div className="flex gap-3 text-xs">
                {!showCc && (
                  <button type="button" className="text-primary hover:underline font-medium" onClick={() => setShowCc(true)}>
                    Cc
                  </button>
                )}
                {!showBcc && (
                  <button type="button" className="text-primary hover:underline font-medium" onClick={() => setShowBcc(true)}>
                    Bcc
                  </button>
                )}
              </div>
            </div>
            <Input required value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" />
          </div>
          {showCc && (
            <AddressRow label="Cc" value={cc} onChange={setCc} onRemove={() => (setShowCc(false), setCc(""))} />
          )}
          {showBcc && (
            <AddressRow
              label="Bcc"
              hint="Hidden from other recipients"
              value={bcc}
              onChange={setBcc}
              onRemove={() => (setShowBcc(false), setBcc(""))}
            />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className={isReply ? "sm:col-span-3 space-y-1.5" : "sm:col-span-2 space-y-1.5"}>
              <Label className="text-xs sm:text-sm font-medium">Subject *</Label>
              <Input required value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            {!isReply && (
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm font-medium">Template</Label>
                <Select value={template} onValueChange={(v) => applyTemplate(v as TemplateKey)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(TEMPLATES) as TemplateKey[]).map((k) => (
                      <SelectItem key={k} value={k}>
                        {TEMPLATES[k].label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs sm:text-sm font-medium">Message *</Label>
            <RichTextEditor
              value={html}
              onChange={(h, t) => {
                setHtml(h);
                setText(t);
              }}
            />
          </div>

          <div className="space-y-1.5">
            {files.map((f, i) => (
              <div key={`${f.file_name}-${i}`} className="flex items-center gap-2 text-xs sm:text-sm rounded-md bg-muted/40 px-3 py-1.5">
                <Paperclip className="size-3.5 text-muted-foreground shrink-0" />
                <span className="truncate flex-1 min-w-0">{f.file_name}</span>
                <span className="text-muted-foreground shrink-0">{Math.ceil(f.size / 1024)} KB</span>
                <button
                  type="button"
                  onClick={() => setFiles((list) => list.filter((_, idx) => idx !== i))}
                  className="text-muted-foreground hover:text-destructive shrink-0"
                  aria-label={`Remove ${f.file_name}`}
                >
                  <X className="size-3.5" />
                </button>
              </div>
            ))}
            <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
            <Button type="button" variant="ghost" size="sm" className="h-8 gap-1.5 text-xs font-medium" onClick={() => fileInput.current?.click()}>
              <Paperclip className="size-3.5" />
              Attach files
            </Button>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={sending} className="gap-1.5">
              {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              Send
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddressRow({
  label,
  hint,
  value,
  onChange,
  onRemove,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs sm:text-sm font-medium">
          {label}
          {hint && <span className="ml-1.5 font-normal text-muted-foreground">({hint})</span>}
        </Label>
        <button type="button" onClick={onRemove} className="text-muted-foreground hover:text-foreground" aria-label={`Remove ${label}`}>
          <X className="size-3.5" />
        </button>
      </div>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Separate multiple addresses with commas" />
    </div>
  );
}
